import React, { useEffect, useRef, useState } from "react";
import { TextField, Label, Dropdown } from "cakemail-ui-components/lib/react";
import { I18n, Translate } from "react-redux-i18n";
import { HexColorPicker } from "react-colorful";
import BoxIcon from "assets/js/scenes/brand/components/BoxIcon";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { updateLocalBrand } from "assets/js/scenes/brand/actions";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { isPartner } from "utils/apiUtils";
import { DEFAULT_COLORS } from "assets/js/scenes/brand/const";

export const BrandColor = (props) => {
	const [state, setState] = useState(getInitialState());
	const [showPicker, setShowPicker] = useState(false);
	const pickerInterval = useRef(null);
	const pickerRef = useRef(null);
	const preSelectedPrimary = useRef(null);

	useEffect(() => {
		setState(getInitialState());
	}, [props.userBrandSDK]);

	useEffect(() => {
		if (showPicker) {
			const viewPortHeight =
				window.innerHeight || document.documentElement.clientHeight;
			const pickerBounds = pickerRef.current.getBoundingClientRect();

			if (viewPortHeight < pickerBounds.top + pickerBounds.height) {
				pickerRef.current.classList.add("top");
			}
		}
	}, [showPicker]);

	useEffect(() => {
		if (preSelectedPrimary.current) {
			handleSDKFieldChange(null, null, {
				...preSelectedPrimary.current,
				skipUnpublisedDelete: true,
			});
		}
	}, [preSelectedPrimary.current]);

	function getInitialState() {
		return {
			color: getColorRef(),
			valid: validateColor(getColorRef()).newValid,
			message: null,
		};
	}

	function getColorRef() {
		if (props?.color?.[0] === "#") {
			return props?.color;
		}

		let foundColor = props.userBrandSDK?.colors?.find(
			(color) => "ref-" + color.id === props?.color
		);

		return foundColor ? foundColor.value : "#fff";
	}

	function validateColor(colour = "") {
		if (!/^#([0-9a-f]{3}){1,2}$/i.test(colour)) {
			return {
				newValid: "error",
				newMessage: I18n.t("app-brand-colour-field-error"),
			};
		} else {
			return { newValid: "valid", newMessage: null };
		}
	}

	function handleColorChange(e) {
		const { value } = e.currentTarget;
		setState({ ...state, color: value });
	}

	function handleColorChangePicker(color) {
		setState({ valid: "valid", message: null, color: color });

		clearTimeout(pickerInterval.current);
		pickerInterval.current = setTimeout(() => {
			handleColorChangeTimeout(null, color);
		}, 500);
	}

	function updateLocalBrand(value, valid, skipUnpublisedDelete = false) {
		if (!props.path) {
			console.warn("Brand color has no path");
		} else {
			if (skipUnpublisedDelete === false && value === props.defaultColor) {
				props.updateLocalBrand(
					props.path,
					value,
					{ value: value, valid: valid },
					true
				);
			} else {
				props.updateLocalBrand(props.path, value, {
					value: value,
					valid: valid,
				});
			}
		}
	}

	function handleColorChangeTimeout(name, value) {
		const { newValid, newMessage } = validateColor(value);
		setState({ color: value, valid: newValid, message: newMessage });

		updateLocalBrand(value, newValid);
	}

	function handleShowPicker(show = true) {
		setShowPicker(show);
	}

	function handleSDKFieldChange(e, input, option) {
		setState({ color: option.color, valid: "valid" });
		updateLocalBrand(option.value, "valid", option.skipUnpublisedDelete);
	}

	let actions = {};
	let options = [];
	let hasASelection = false;
	if (!isPartner()) {
		if (props.userBrandSDK) {
			options =
				props.userBrandSDK?.colors?.map((color) => {
					const id = "ref-" + color.id;
					const selected = props.color
						? "ref-" + color.id === props.color
						: props.usePrimary && color.tag === "Primary";

					if (
						props.usePrimary &&
						!props.color &&
						color.tag === "Primary" &&
						!preSelectedPrimary.current
					) {
						preSelectedPrimary.current = { color: color.value, value: id };
					}
					if (!hasASelection) {
						hasASelection = selected;
					}
					return {
						label: (
							<>
								<span>{color.name}</span>{" "}
								{color.tag && <Label text={color.tag} />}
							</>
						),
						value: id,
						icon: <BoxIcon color={color.value} />,
						color: color.value,
						selected: selected,
					};
				}) || [];
		}

		options.unshift({
			className: "sectionTitle",
			label: <Translate value="app-brand-colour-field-section-title-brand" />,
		});

		options.push({
			className: "addNew",
			label: <Translate value="app-brand-colour-field-add-new" />,
			onClick: () => {
				props.goToSDK("");
			},
		});

		options.push({
			className: "sectionTitle",
			label: <Translate value="app-brand-colour-field-section-title-system" />,
		});

		Object.keys(DEFAULT_COLORS).forEach((key) => {
			let selected = false;
			if (!hasASelection) {
				if (DEFAULT_COLORS[key] === props.color) {
					selected = true;
					hasASelection = true;
				} else if (props.placeholderColor === DEFAULT_COLORS[key]) {
					selected = true;
				}
			}
			options.push({
				label: (
					<span>
						<Translate value={`app-brand-colour-system-${key.toLowerCase()}`} />
					</span>
				),
				value: DEFAULT_COLORS[key],
				icon: <BoxIcon color={DEFAULT_COLORS[key]} />,
				color: DEFAULT_COLORS[key],
				selected: selected,
			});
		});
	} else {
		actions = {
			onFocus: handleShowPicker,
			onBlur: () => {
				handleShowPicker(false);
			},
		};
	}

	return (
		<div className="brandElement-color" {...actions}>
			{props.userBrandSDK || !isPartner() ? (
				<>
					<BoxIcon
						color={hasASelection ? state.color : props.placeholderColor}
					/>
					<Dropdown
						key={`${props.userBrandSDK ? "sdkLoaded" : ""}`}
						type="custom"
						className="brandColorDropdown"
						shapeSytle="outline"
						options={options}
						placeholder={
							props.fieldPlaceholder
								? props.fieldPlaceholder
								: I18n.t("app-brand-colour-field-placeholder")
						}
						onChange={handleSDKFieldChange}
					/>
				</>
			) : (
				<>
					<TextField
						onChange={handleColorChange}
						valid={state.valid}
						message={state.message}
						timeoutOptions={{ callback: handleColorChangeTimeout }}
						value={state.color}
						placeholder={
							props.fieldPlaceholder
								? props.fieldPlaceholder
								: I18n.t("app-brand-colour-field-placeholder")
						}
					/>

					<BoxIcon
						color={hasASelection ? state.color : props.placeholderColor}
					/>
					{showPicker && (
						<div className="colorPickerContainer" ref={pickerRef}>
							<HexColorPicker
								color={state.color}
								onChange={handleColorChangePicker}
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
};

BrandColor.propTypes = {
	color: PropTypes.string,
	defaultColor: PropTypes.string,
	path: PropTypes.string,
	updateLocalBrand: PropTypes.func,
	userBrandSDK: PropTypes.object,
	fieldPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	goToSDK: PropTypes.func,
	usePrimary: PropTypes.bool,
	placeholderColor: PropTypes.string,
};

BrandColor.defaultProps = {
	placeholderColor: DEFAULT_COLORS.BLACK,
	fieldPlaceholder: "",
	goToSDK: () => {},
	usePrimary: false,
};

export default withRouter(
	connect(
		(state) => {
			return {
				userBrandSDK: state.userBrandSDK,
			};
		},
		(dispatch) =>
			bindActionCreators(
				{
					updateLocalBrand,
				},
				dispatch
			)
	)(BrandColor)
);
